import { useCallback, useEffect, useState } from 'react';
import { organizationAPI } from '../../services/api';
import { LoadError, OrgStore, Organization, loadErrorOf } from './orgTypes';
import { athensDaysAgoYmd, athensYmd } from '../../utils/athensDate';

/**
 * «Παραγγελίες & παραστατικά» των κεντρικών: κεντρική εικόνα ανά κατάστημα και ακύρωση από τα κεντρικά.
 * Η κατάσταση ζει στο OrgDashboard όπως πριν (τα φίλτρα μένουν όταν αλλάζεις καρτέλα) και ο server ρωτιέται
 * μόνο όσο είναι ανοιχτή η καρτέλα — ίδια endpoints, ίδια query (from/to/include_failed/doc_type).
 */

export type OrdersView = 'orders' | 'fiscal';
export type DocTypeFilter = 'all' | 'receipts' | 'invoices' | 'credit' | 'order_slips';

export interface OrgOrdersState {
  storeId: number | null;
  setStoreId: (id: number | null) => void;
  view: OrdersView;
  setView: (v: OrdersView) => void;
  from: string;
  to: string;
  setRange: (from: string, to: string) => void;
  rows: any[];
  loading: boolean;
  error: LoadError | null;
  busy: number | null;
  setBusy: (id: number | null) => void;
  showFailed: boolean;
  setShowFailed: (fn: (v: boolean) => boolean) => void;
  phantomCount: number;
  docType: DocTypeFilter;
  setDocType: (d: DocTypeFilter) => void;
  counts: Record<string, number>;
  reload: () => Promise<void>;
}

export function useOrgOrders(selectedOrg: Organization | null, stores: OrgStore[], active: boolean): OrgOrdersState {
  const [storeId, setStoreId] = useState<number | null>(null);
  const [view, setView] = useState<OrdersView>('orders');
  // Ημέρες Ελλάδας (ως 24/10/2026 UTC: από 00:00 ως 03:00 η προεπιλογή έδειχνε «χθες»).
  const [from, setFrom] = useState<string>(() => athensDaysAgoYmd(6));
  const [to, setTo] = useState<string>(() => athensYmd());
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<LoadError | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  // Απορριφθείσες προσπάθειες κάρτας («φαντάσματα»): κρυφές, εκτός αν ζητηθούν.
  const [showFailed, setShowFailedState] = useState(false);
  const [phantomCount, setPhantomCount] = useState(0);
  // Φίλτρο τύπου παραστατικού (Αποδείξεις / Τιμολόγια / Πιστωτικά) + πλήθη ανά ομάδα, ώστε ο χρήστης
  // να βλέπει τι υπάρχει πριν φιλτράρει.
  const [docType, setDocType] = useState<DocTypeFilter>('all');
  const [counts, setCounts] = useState<Record<string, number>>({});

  const load = useCallback(async () => {
    if (!selectedOrg || !storeId) { setRows([]); return; }
    setLoading(true);
    try {
      if (view === 'orders') {
        const data = await organizationAPI.storeOrders(selectedOrg.id, storeId, from, to, undefined, showFailed);
        setRows(data.orders || []);
        setPhantomCount(data.phantom_count || 0);
      } else {
        const data = await organizationAPI.storeFiscal(selectedOrg.id, storeId, from, to, docType);
        setRows(data.documents || []);
        setCounts(data.counts || {});
        setPhantomCount(0);
      }
      setError(null);
    } catch (err) {
      // Μηδενίζουμε ΚΑΙ τα πλήθη: αλλιώς, αν αποτύχει η κλήση, τα κουμπιά συνεχίζουν να δείχνουν τα νούμερα
      // του προηγούμενου καταστήματος.
      setRows([]);
      setCounts({});
      setError(loadErrorOf(err));
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, storeId, view, from, to, showFailed, docType]);

  useEffect(() => {
    if (!active) return;
    // Πρώτο άνοιγμα (ή κατάστημα που δεν ανήκει πια στον οργανισμό, π.χ. μετά από αλλαγή οργανισμού): το πρώτο.
    if (stores.length > 0 && (storeId == null || !stores.some(s => s.id === storeId))) {
      setStoreId(stores[0].id);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ίδια σημεία επαναφόρτωσης με πριν
  }, [active, storeId, view, from, to, showFailed, docType, stores]);

  return {
    storeId,
    setStoreId,
    view,
    setView,
    from,
    to,
    setRange: (f, tt) => { setFrom(f); setTo(tt); },
    rows,
    loading,
    error,
    busy,
    setBusy,
    showFailed,
    setShowFailed: fn => setShowFailedState(fn),
    phantomCount,
    docType,
    setDocType,
    counts,
    reload: load,
  };
}
